import { FC, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import type {
  Decision,
  DecisionPayload,
  FileCandidate,
  Proposal,
} from 'src/services/DataVaultTransfer.service';
import PDFPreviewModal from './PDFPreviewModal';

interface TransferFileProposalProps {
  proposal: Proposal;
  decision: Decision | undefined;
  onSave: (payload: DecisionPayload) => void;
  /**
   * Incremented by the page when a decision write fails. Every proposal
   * re-reads its state from the server's decision, which is how a failed save
   * is undone instead of leaving the row showing something the server does not
   * have.
   */
  rollbackNonce?: number;
}

interface FileSelectionState {
  fileSelected: boolean;
  childrenSelected: Record<number, boolean>;
}

// Shape of each entry stored in Decision.files_payload for files_selected decisions.
interface SavedFilePayloadEntry {
  file_hash: string;
  children: Array<{ child_qit_id: number }>;
}

function buildInitialState(
  files: FileCandidate[]
): Record<number, FileSelectionState> {
  const state: Record<number, FileSelectionState> = {};
  files.forEach((file, idx) => {
    const childrenSelected: Record<number, boolean> = {};
    file.children.forEach((_child, cIdx) => {
      childrenSelected[cIdx] = true;
    });
    state[idx] = { fileSelected: true, childrenSelected };
  });
  return state;
}

function buildStateFromDecision(
  files: FileCandidate[],
  decision: Decision | undefined
): Record<number, FileSelectionState> {
  const base = buildInitialState(files);

  if (
    !decision ||
    decision.decision !== 'files_selected' ||
    !decision.files_payload
  ) {
    return base;
  }

  const payload = decision.files_payload as SavedFilePayloadEntry[];
  if (!Array.isArray(payload)) {
    return base;
  }

  const payloadByHash = new Map<string, SavedFilePayloadEntry>();
  for (const entry of payload) {
    payloadByHash.set(entry.file_hash, entry);
  }

  const result: Record<number, FileSelectionState> = {};
  files.forEach((file, idx) => {
    const saved = payloadByHash.get(file.file_hash);
    if (!saved) {
      // File not in payload — deselected
      const childrenSelected: Record<number, boolean> = {};
      file.children.forEach((_child, cIdx) => {
        childrenSelected[cIdx] = false;
      });
      result[idx] = { fileSelected: false, childrenSelected };
      return;
    }

    const childrenSelected: Record<number, boolean> = {};
    file.children.forEach((child, cIdx) => {
      childrenSelected[cIdx] = saved.children.some(
        (sc) => sc.child_qit_id === child.child_qit_id
      );
    });
    result[idx] = { fileSelected: true, childrenSelected };
  });

  return result;
}

function buildFilesPayload(
  files: FileCandidate[],
  selectionState: Record<number, FileSelectionState>
): unknown {
  return files
    .map((file, originalIdx) => ({ file, originalIdx }))
    .filter(({ originalIdx }) => selectionState[originalIdx]?.fileSelected)
    .map(({ file, originalIdx }) => ({
      included: true,
      file_hash: file.file_hash,
      source_survey_file_id: file.source_survey_file_id,
      children: file.children
        .filter(
          (_, cIdx) =>
            selectionState[originalIdx]?.childrenSelected[cIdx] === true
        )
        .map((child) => ({
          included: true,
          child_qit_id: child.child_qit_id,
          source_survey_answer_id: child.source_survey_answer_id,
        })),
    }));
}

const TransferFileProposal: FC<TransferFileProposalProps> = ({
  proposal,
  decision,
  onSave,
  rollbackNonce = 0,
}) => {
  const { qit_id, instance_index, question_label, files } = proposal;
  const [selectionState, setSelectionState] = useState<
    Record<number, FileSelectionState>
  >(() => buildStateFromDecision(files ?? [], decision));
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Sync local state when decision changes externally (e.g. "Discard all" /
  // "Confirm all"), and when a save fails — rollbackNonce is bumped by the page
  // so a rejected write snaps the row back to what the server actually holds.
  useEffect(() => {
    setSelectionState(buildStateFromDecision(files ?? [], decision));
  }, [decision, files, rollbackNonce]);

  const saveSelection = useCallback(
    (newState: Record<number, FileSelectionState>) => {
      const filesPayload = buildFilesPayload(files ?? [], newState);
      onSave({
        qit_id,
        instance_index,
        decision: 'files_selected',
        files_payload: filesPayload,
      });
    },
    [qit_id, instance_index, files, onSave]
  );

  const handleFileToggle = useCallback(
    (fileIdx: number) => {
      setSelectionState((prev) => {
        const updated = {
          ...prev,
          [fileIdx]: {
            ...prev[fileIdx],
            fileSelected: !prev[fileIdx]?.fileSelected,
          },
        };
        saveSelection(updated);
        return updated;
      });
    },
    [saveSelection]
  );

  const handleChildToggle = useCallback(
    (fileIdx: number, childIdx: number) => {
      setSelectionState((prev) => {
        const updated = {
          ...prev,
          [fileIdx]: {
            ...prev[fileIdx],
            childrenSelected: {
              ...prev[fileIdx]?.childrenSelected,
              [childIdx]: !prev[fileIdx]?.childrenSelected[childIdx],
            },
          },
        };
        saveSelection(updated);
        return updated;
      });
    },
    [saveSelection]
  );

  if (!files || files.length === 0) {
    return null;
  }

  // Determine state for consistent visual styling
  const hasDecision = decision?.decision === 'files_selected';
  const hasFilesSelected =
    hasDecision &&
    Array.isArray(decision.files_payload) &&
    (decision.files_payload as unknown[]).length > 0;
  const isConfirmed = hasFilesSelected;
  const isDiscarded = hasDecision && !hasFilesSelected;
  // A file the renter unchecked is the moment they expect somewhere to put the
  // replacement. The upload lives in the interview, so say so at that moment
  // rather than leaving them on a screen that cannot accept a document.
  const anyFileUnchecked = files.some(
    (_file, idx) => selectionState[idx]?.fileSelected === false
  );

  return (
    <Box
      sx={(theme) => ({
        px: 2,
        pl: 5.25,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'grey.100',
        borderLeft: isConfirmed ? '3px solid' : 'none',
        borderLeftColor: isConfirmed ? 'success.light' : undefined,
        bgcolor: isDiscarded
          ? alpha(theme.palette.error.main, 0.03)
          : 'background.paper',
        opacity: isDiscarded ? 0.6 : 1,
        transition: 'background-color 0.2s, opacity 0.2s, border-color 0.2s',
      })}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: '0.6875rem' }}
      >
        {question_label}
      </Typography>
      {files.map((file, fileIdx) => {
        const fileState = selectionState[fileIdx];
        const isFileChecked = fileState?.fileSelected ?? false;
        const hiddenChildrenCount = file.children.length;

        return (
          <Box key={file.file_hash} sx={{ mt: 0.75 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFileChecked}
                    onChange={() => handleFileToggle(fileIdx)}
                    size="small"
                    inputProps={{
                      'aria-label': file.filename || `File ${fileIdx + 1}`,
                    }}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={500}>
                    {file.filename || `File ${fileIdx + 1}`}
                  </Typography>
                }
              />
              {file.pdf_file_url && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setPreviewUrl(file.pdf_file_url)}
                  aria-label={`Preview ${file.filename}`}
                  sx={{ fontSize: '0.6875rem', minWidth: 0, px: 1 }}
                >
                  Preview
                </Button>
              )}
            </Stack>

            {!isFileChecked && hiddenChildrenCount > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 4, fontSize: '0.625rem' }}
              >
                {hiddenChildrenCount} answer
                {hiddenChildrenCount !== 1 ? 's' : ''} related to this file
              </Typography>
            )}

            {isFileChecked && file.children.length > 0 && (
              <Box sx={{ ml: 4 }}>
                {file.children.map((child, childIdx) => {
                  const isChildChecked =
                    fileState?.childrenSelected[childIdx] === true;
                  return (
                    <FormControlLabel
                      key={child.source_survey_answer_id}
                      control={
                        <Checkbox
                          checked={isChildChecked}
                          onChange={() => handleChildToggle(fileIdx, childIdx)}
                          size="small"
                          inputProps={{
                            'aria-label': child.child_question_label,
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.6875rem' }}
                        >
                          {child.child_question_label}: {child.display_value}
                        </Typography>
                      }
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}

      {anyFileUnchecked && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.75, fontSize: '0.625rem' }}
        >
          We will ask for this again in your interview, where you can upload a
          new document.
        </Typography>
      )}

      <PDFPreviewModal
        open={previewUrl !== null}
        url={previewUrl}
        onClose={() => setPreviewUrl(null)}
      />
    </Box>
  );
};

export default TransferFileProposal;
